// Credit management service

import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/plugins/firebase";
import { v4 as uuidv4 } from "uuid";

class CreditService {
  calculateCreditBalance(creditHistory) {
    if (!creditHistory || !creditHistory.length) return 0;

    return creditHistory.reduce((balance, transaction) => {
      // Only consider completed transactions
      if (transaction.status !== "completed") return balance;

      // Add credits for purchases and refunds
      if (transaction.category === "credit_added") {
        return balance + transaction.amount;
      }

      // Subtract credits for usage
      if (transaction.category === "credit_spent") {
        return balance - transaction.amount;
      }

      return balance;
    }, 0);
  }

  async handleCreditPurchase(
    userId,
    selectedPlan,
    selectedPlanData,
    credit,
    pricePerCredit,
    totalPrice,
    productId
  ) {
    try {
      // Generate a unique reference ID
      const referenceId = `CA${Date.now()}`;
      console.log("referenceId", referenceId);

      // Create pending credit transaction object
      const creditTransaction = {
        id: uuidv4(),
        userId: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        referenceId: referenceId,
        category: "credit_added",
        type: "purchase",
        status: "pending", // Set as pending initially
        paymentMethod: "stripe",
        amount: selectedPlan === 0 ? credit : selectedPlanData.flightCredits,
        pricePerCredit: pricePerCredit,
        totalPrice: totalPrice,
        planDetails: selectedPlanData
          ? {
              planId: selectedPlanData.id,
              planName: `${selectedPlanData.flightCredits} Credits Pack`,
              savings: selectedPlanData.savePercentage || 0,
            }
          : null,
      };

      // Add transaction to credits collection
      const creditRef = await addDoc(
        collection(db, "credits"),
        creditTransaction
      );

      console.log("selectedPlanData", selectedPlanData);

      // Generate success and cancel URLs
      const successUrl = `${window.location.origin}/credits?status=success&reference=${referenceId}&creditId=${creditRef.id}`;
      const cancelUrl = `${window.location.origin}/credits?status=cancelled&reference=${referenceId}&creditId=${creditRef.id}`;

      const response = await fetch(
        // "http://localhost:3001/v1/payments/create-session",
        "https://skyhost.ai/create-session",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            totalPrice: totalPrice,
            creditAmount:
              selectedPlan === 0 ? credit : selectedPlanData.flightCredits,
            pricePerCredit: pricePerCredit,
            successUrl: successUrl,
            cancelUrl: cancelUrl,
            referenceId: referenceId,
            creditId: creditRef.id,
            productId: productId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const result = await response.json();

      if (!result.status) {
        throw new Error(result.message || "Failed to create payment session");
      }

      // Redirect to Stripe checkout using the session URL from backend
      window.location.href = result.data.sessionUrl;

      return {
        success: true,
        referenceId: referenceId,
        creditId: creditRef.id,
      };
    } catch (error) {
      console.error("Error creating credit transaction:", error);
      throw error;
    }
  }

  async consumeCredit(userId, creditBalance, reservationDetails) {
    try {
      if (creditBalance < 1) {
        throw new Error("Insufficient credits");
      }

      const creditConsumption = {
        id: uuidv4(),
        userId: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        referenceId: `CS${Date.now()}`,
        category: "credit_spent",
        type: "usage",
        status: "completed",
        amount: reservationDetails.creditUsed,
        reservationId: reservationDetails.id,
        description: reservationDetails.description,
      };

      const creditRef = await addDoc(
        collection(db, "credits"),
        creditConsumption
      );
      return creditRef.id;
    } catch (error) {
      console.error("Error consuming credit:", error);
      throw error;
    }
  }

  async refundCredit(userId, creditHistory, reservationId) {
    try {
      // Find the latest completed purchase transaction
      const lastPurchase = creditHistory.find(
        (transaction) =>
          transaction.reservationId === reservationId &&
          transaction.status === "completed"
      );

      if (!lastPurchase) {
        throw new Error("No matching reservation found to refund");
      }

      const creditRefund = {
        id: uuidv4(),
        userId: userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        referenceId: `CR${Date.now()}`,
        category: "credit_added",
        type: "refund",
        status: "completed",
        amount: lastPurchase.amount,
        originalTransaction: {
          id: lastPurchase.id,
          referenceId: lastPurchase.referenceId,
          date: lastPurchase.createdAt,
        },
      };

      const creditRef = await addDoc(collection(db, "credits"), creditRefund);
      return creditRef.id;
    } catch (error) {
      console.error("Error refunding credit:", error);
      throw error;
    }
  }
}

export const creditService = new CreditService();
export default creditService;
